import React, { useCallback } from "react";
import { useHistory } from "react-router-dom";
import { useIntercom } from "react-use-intercom";
import * as Routes from "routes";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { FormState, Season, Style } from "../step-config-file/types";

interface FormComponentProps {
  formState: FormState;
  onFormChange: (newState: Partial<FormState>) => void;
}

export const FormComponent: React.FC<FormComponentProps> = ({
  formState,
  onFormChange,
}) => {
  const history = useHistory();
  const { showNewMessage } = useIntercom();

  const updateStyleKeyWord = useCallback(
    (style: Style, people: number, season: Season): string | undefined => {
      if (style === Style.Co2Annually) {
        return `${style}_${people}_${season}`;
      }
      return style;
    },
    [],
  );

  const handleStyleChange = useCallback(
    (value: string) => {
      const updatedState: Partial<FormState> = { style: value as Style };
      const newStyleKeyWord = updateStyleKeyWord(
        value as Style,
        formState.people,
        formState.season,
      );
      if (newStyleKeyWord) {
        updatedState.style_key_word = newStyleKeyWord;
      }
      onFormChange(updatedState);
    },
    [formState, onFormChange, updateStyleKeyWord],
  );

  const menuItems: { key: string; value: string }[] = [
    { key: "Emitted CO₂", value: Style.Co2Annually },
    { key: "Solar Panel", value: Style.SolarPanel },
    { key: "Swimming Pool", value: Style.Pool },
    { key: "Roof Colour", value: Style.RoofColour },
  ];

  return (
    <Card className="w-40">
      <CardContent className="space-y-2">
        <div className="flex items-center gap-3">
          <Switch
            id="extrude"
            checked={formState.extrude}
            onCheckedChange={(checked) => onFormChange({ extrude: checked })}
          />
          <Label htmlFor="extrude">Extrude?</Label>
        </div>

        <div className="space-y-1.5">
          <Label className="text-muted-foreground">Style By</Label>
          <Select value={formState.style} onValueChange={handleStyleChange}>
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {menuItems.map((s) => (
                <SelectItem key={s.value} value={s.value}>
                  {s.key}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Button
          className="w-full"
          onClick={() =>
            showNewMessage(`I'd like to talk to someone about "Emissions Data"`)
          }
        >
          Talk to an expert
        </Button>
        <Button
          variant="outline"
          className="w-full"
          onClick={() => {
            history.push(Routes.demosPage);
          }}
        >
          Exit Demo
        </Button>
      </CardContent>
    </Card>
  );
};
